/**
 * Shared primitives for the revamped screens. Everything here is presentation
 * only — no data fetching, no routing.
 */
import React from "react";
import ReactModal from "react-modal";

import { CloseIcon } from "./icons";

// --- Button -----------------------------------------------------------------
const BUTTON_VARIANTS = {
  primary: "bg-primary-600 text-white hover:bg-primary-700 disabled:bg-gray-200 disabled:text-gray-400",
  secondary:
    "border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:text-gray-300 disabled:hover:bg-white",
  danger: "bg-red-600 text-white hover:bg-red-700 disabled:bg-gray-200 disabled:text-gray-400",
  ghost: "text-primary-600 hover:bg-primary-50 disabled:text-gray-300",
  dangerGhost: "text-red-600 hover:bg-red-50 disabled:text-gray-300",
};

const BUTTON_SIZES = {
  sm: "px-3 py-1.5 text-xs gap-1.5",
  md: "px-4 py-2.5 text-sm gap-2",
  lg: "px-5 py-3 text-sm gap-2",
};

export const Button = ({
  variant = "primary",
  size = "md",
  as: Tag = "button",
  className = "",
  loading = false,
  disabled,
  children,
  ...rest
}) => (
  <Tag
    disabled={Tag === "button" ? disabled || loading : undefined}
    className={`inline-flex items-center justify-center rounded-lg font-semibold transition disabled:cursor-not-allowed ${
      BUTTON_VARIANTS[variant]
    } ${BUTTON_SIZES[size]} ${className}`}
    {...rest}
  >
    {loading ? <Spinner className="h-4 w-4" /> : children}
  </Tag>
);

// --- Spinner ----------------------------------------------------------------
export const Spinner = ({ className = "h-5 w-5" }) => (
  <span
    role="status"
    aria-label="Loading"
    className={`inline-block animate-spin rounded-full border-2 border-current border-r-transparent opacity-70 ${className}`}
  />
);

export const PageSpinner = () => (
  <div className="flex min-h-[50vh] items-center justify-center text-primary-600">
    <Spinner className="h-7 w-7" />
  </div>
);

// --- Card -------------------------------------------------------------------
export const Card = ({ className = "", children, ...rest }) => (
  <div className={`card ${className}`} {...rest}>
    {children}
  </div>
);

// --- Chip -------------------------------------------------------------------
const CHIP_TONES = {
  neutral: "bg-gray-100 text-gray-600",
  brand: "bg-primary-50 text-primary-700",
  good: "bg-teal-50 text-teal-700",
  warning: "bg-amber-50 text-amber-700",
  critical: "bg-red-50 text-red-700",
};

export const Chip = ({ tone = "neutral", className = "", children }) => (
  <span className={`status-chip ${CHIP_TONES[tone]} ${className}`}>{children}</span>
);

// --- Field ------------------------------------------------------------------
export const Field = ({ label, error, hint, htmlFor, children, className = "" }) => (
  <div className={`mb-4 ${className}`}>
    {label && (
      <label className="field-label" htmlFor={htmlFor}>
        {label}
      </label>
    )}
    {children}
    {error ? (
      <p className="mt-1 text-xs font-medium text-red-600">{error}</p>
    ) : (
      hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>
    )}
  </div>
);

export const TextInput = ({ invalid, className = "", ...rest }) => (
  <input className={`field-input ${invalid ? "field-input-invalid" : ""} ${className}`} {...rest} />
);

// --- Toggle -----------------------------------------------------------------
export const Toggle = ({ checked, onChange, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    onClick={() => onChange(!checked)}
    className={`relative h-6 w-11 flex-shrink-0 rounded-full transition ${checked ? "bg-teal-500" : "bg-gray-200"}`}
  >
    <span
      className={`absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
        checked ? "translate-x-5" : ""
      }`}
    />
  </button>
);

// --- Stepper ----------------------------------------------------------------
export const Stepper = ({ value, min = 0, max = 999, onChange, label }) => (
  <div className="flex items-center gap-3">
    <button
      type="button"
      aria-label={`Decrease ${label}`}
      onClick={() => onChange(Math.max(min, value - 1))}
      className="h-9 w-9 rounded-lg border border-gray-200 text-lg font-bold leading-none text-primary-600 transition hover:bg-gray-50"
    >
      −
    </button>
    <input
      aria-label={label}
      inputMode="numeric"
      value={value}
      onChange={(e) => onChange(Math.min(max, Number(e.target.value.replace(/\D/g, "") || 0)))}
      className="w-20 rounded-lg border border-gray-200 px-3 py-2 text-center text-sm focus:border-primary-500 focus:outline-none"
    />
    <button
      type="button"
      aria-label={`Increase ${label}`}
      onClick={() => onChange(Math.min(max, value + 1))}
      className="h-9 w-9 rounded-lg border border-gray-200 text-lg font-bold leading-none text-primary-600 transition hover:bg-gray-50"
    >
      +
    </button>
  </div>
);

// --- Modal ------------------------------------------------------------------
/**
 * Wraps react-modal so every dialog in the app gets the same chrome. Full-screen
 * sheet on a phone, centred card from `nav` up.
 */
export const Modal = ({ isOpen, onRequestClose, title, children, footer, size = "md", ...rest }) => {
  const width = { sm: "nav:max-w-sm", md: "nav:max-w-md", lg: "nav:max-w-lg", xl: "nav:max-w-2xl" }[size];
  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      closeTimeoutMS={200}
      overlayClassName="fixed inset-0 z-50 flex items-end nav:items-center justify-center bg-primary-900/45 p-0 nav:p-4"
      className={`w-full ${width} max-h-[92vh] overflow-y-auto rounded-t-2xl nav:rounded-2xl bg-white outline-none animate-sheetup nav:animate-none`}
      {...rest}
    >
      {title && (
        <div className="sticky top-0 flex items-center justify-between gap-4 border-b border-gray-100 bg-white px-6 py-4">
          <h2 className="text-base font-bold text-primary-900">{title}</h2>
          {onRequestClose && (
            <button onClick={onRequestClose} aria-label="Close" className="text-gray-400 transition hover:text-gray-600">
              <CloseIcon className="h-5 w-5" />
            </button>
          )}
        </div>
      )}
      <div className="px-6 py-5">{children}</div>
      {footer && (
        <div className="sticky bottom-0 flex items-center justify-end gap-3 border-t border-gray-100 bg-white px-6 py-4">
          {footer}
        </div>
      )}
    </ReactModal>
  );
};

// --- Empty state ------------------------------------------------------------
export const EmptyState = ({ icon: Icon, title, description, action }) => (
  <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
    {Icon && (
      <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 text-gray-400">
        <Icon className="h-6 w-6" />
      </span>
    )}
    <p className="text-sm font-semibold text-primary-900">{title}</p>
    {description && <p className="mt-1 max-w-sm text-xs text-gray-500">{description}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

// --- Section heading --------------------------------------------------------
export const SectionTitle = ({ children, action }) => (
  <div className="mb-4 flex items-center justify-between gap-4">
    <p className="text-sm font-bold text-primary-900">{children}</p>
    {action}
  </div>
);
